import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { TABLE_BORDER_RADIUS } from '@databases/styles';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import styled, { css } from 'styled-components';

export const ButtonGroup = styled.div<{
  align?: 'left' | 'right' | 'spaced';
  noShrink?: boolean;
  isCompact?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${(props) =>
    props.align === 'right'
      ? 'flex-end'
      : props.align === 'spaced'
      ? 'space-between'
      : 'flex-start'};
  width: ${(props) => (props.noShrink ? 'max-content' : 'auto')};
  column-gap: ${(props) => (props.isCompact ? '4px' : '10px')};
`;

export const tableRowMinHeight = 42;
export const newTableBorderWidth = 1;
export const subtleBorder = `2px solid ${token('color.border', '#091E4224')}`;

export const gridTable = css`
  display: grid;

  > thead,
  > tbody,
  > tfoot {
    &,
    > tr {
      display: contents;
    }
  }
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const SubtleTable = styled.table<{
  dragHandlerTdElementWidth: number;
  isVisualRefreshM1Enabled: boolean;
}>`
  border-collapse: separate; // Otherwise the border is not shown for sticky cells
  border-spacing: 0;

  > thead,
  > tbody,
  > tfoot {
    border: none;
    display: contents; // To allow for top row to be sticky to the table, not to the thead
  }

  > thead > tr {
    position: sticky;
    top: 0;
    background: ${token('elevation.surface', '#FFFFFF')};
  }

  > thead > tr {
    border-start-start-radius: ${TABLE_BORDER_RADIUS};
    border-start-end-radius: ${TABLE_BORDER_RADIUS};
  }

  > thead > tr > th {
    font: ${token('font.heading.xxsmall')};

    &.${IGNORED_TABLE_ELEMENT_CLASS} {
      border: none;
    }
  }

  > tbody > tr > * {
    &.${IGNORED_TABLE_ELEMENT_CLASS} {
      width: ${(props) => props.dragHandlerTdElementWidth / 2}px;
      background: ${token('elevation.surface', '#FFFFF')};
      border: none;
    }
  }

  > tfoot > tr > td {
    &:first-child {
      border-end-start-radius: ${TABLE_BORDER_RADIUS};
    }

    &:last-child {
      border-right: ${subtleBorder};
      border-width: ${newTableBorderWidth}px;
      border-end-end-radius: ${TABLE_BORDER_RADIUS};
      margin-right: 1px;
    }
  }

  > * > tr > * {
    border: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
  }

  > thead + tbody > tr:first-child > *,
  tbody > tr + tr > *,
  tfoot > tr:first-child > * {
    border-top: none;
  }

  > * > tr > * {
    border-right: none;
  }

  > * > tr > .entry-number-table-header,
  > * > tr > .entry-number-table-data {
    border-right: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
    padding: 12px 0px;
    display: flex;
    align-items: center;
  }

  > thead > tr:hover > th {
    ${(props) =>
      !props.isVisualRefreshM1Enabled &&
      `background: ${token('elevation.surface.hovered', '#F1F2F4')}`};
  }

  > thead > tr:hover > th.${IGNORED_TABLE_ELEMENT_CLASS} {
    background: ${token('elevation.surface', '#FFFFFF')};
  }
`;

export const FloatingButtonContainer = styled.div`
  background-color: ${colors.N0};
  border-radius: ${token('radius.small', '3px')};
  box-shadow: 0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A};
  box-sizing: border-box;

  button:not(:hover, :focus),
  a:not(:hover, :focus) {
    background: ${token('elevation.surface', '#FFFFFF')};
  }
`;

export const ErrorMessage = styled.div`
  color: ${token('color.text.danger')};
  white-space: pre-line;
`;

export const DropZone = styled.div<{ isDragActive: boolean; isHidden?: boolean }>`
  display: flex;
  flex-direction: column;
  border: ${token('border.width.selected', '2px')} dashed
    ${(props) =>
      props.isDragActive ? colors.N300 : !props.isHidden ? colors.N80 : 'transparent'};
  border-radius: ${token('radius.small', '3px')};
  background-color: ${(props) => (props.isDragActive ? colors.B50 : 'transparent')};
  transition: border 0.2s;
`;
